import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../ThemeContext';
import { ModalShell } from '../ui';

const PHRASE = "I believe in a future where I own my intellectual property";

function AdaFaucetFlow({ onBack, t }) {
  const [step, setStep] = useState(1);
  const [typed, setTyped] = useState('');
  const [address, setAddress] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');
  const inputRef = useRef(null);

  const phraseMatch = typed.trim().toLowerCase() === PHRASE.toLowerCase();
  const validAddress = address.trim().startsWith('addr') && address.trim().length > 50;

  useEffect(() => {
    if (step === 1 && inputRef.current) inputRef.current.focus();
  }, [step]);

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await fetch('/api/faucet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          walletAddress: address.trim(),
          phraseConfirmed: true,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Request failed');
      }
      setTxHash(data.txHash || '');
      setSent(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          border: '2px solid #22c55e',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 20px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12l5 5L20 7"/>
          </svg>
        </div>
        <h3 style={{
          fontFamily: 'Crimson Pro, serif',
          fontSize: '28px',
          fontWeight: 700,
          color: t.text,
          marginBottom: '8px',
        }}>1 ADA Sent</h3>
        <p style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '14px',
          color: t.textSecondary,
          marginBottom: '8px',
          lineHeight: 1.6,
        }}>
          Welcome to Limestone. Your ADA should arrive in about 30 seconds.
        </p>
        <p style={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '12px',
          color: t.textTertiary || t.textSecondary,
          marginBottom: '24px',
        }}>
          This is a one-time gift from The Quarry.
        </p>
        {txHash && (
          <a
            href={`https://cardanoscan.io/transaction/${txHash}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '12px',
              color: t.accent || t.text,
              display: 'block',
              marginBottom: '20px',
            }}
          >
            View on CardanoScan
          </a>
        )}
        <button
          onClick={onBack}
          style={{
            backgroundColor: t.buttonBg,
            color: t.buttonText,
            padding: '14px 32px',
            borderRadius: '12px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: 600,
            fontFamily: 'Poppins, sans-serif',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.transform = 'translateY(-2px)'}
          onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
        >Connect Wallet</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={t.accent || t.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
        </svg>
        <h2 style={{
          fontFamily: 'Crimson Pro, serif',
          fontSize: '28px',
          fontWeight: 700,
          color: t.text,
        }}>Get Started with ADA</h2>
      </div>
      <p style={{
        fontFamily: 'Poppins, sans-serif',
        fontSize: '13px',
        color: t.textSecondary,
        marginBottom: '28px',
        lineHeight: 1.6,
      }}>
        Limestone uses Cardano for authentication. You need at least 1 ADA in your wallet to connect. We'll send you 1 ADA to get started.
      </p>

      {/* Step indicator */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px' }}>
        {[1, 2].map(s => (
          <div key={s} style={{
            flex: 1,
            height: '3px',
            borderRadius: '999px',
            backgroundColor: step >= s ? (t.accent || t.buttonBg) : (t.borderStrong || t.border),
            transition: 'background-color 0.3s',
          }} />
        ))}
      </div>

      {/* Step 1: Type the phrase */}
      {step === 1 && (
        <div>
          <p style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            color: t.text,
            marginBottom: '12px',
          }}>Type this to continue:</p>

          <div style={{
            backgroundColor: t.modalBg || t.surface,
            border: `1px solid ${t.borderStrong || t.border}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
          }}>
            <p style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '15px',
              color: t.accent || t.text,
              lineHeight: 1.6,
              fontWeight: 500,
            }}>
              {PHRASE}
            </p>
          </div>

          <div style={{ position: 'relative', marginBottom: '8px' }}>
            <textarea
              ref={inputRef}
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder="Type the statement above..."
              rows={3}
              style={{
                width: '100%',
                backgroundColor: t.inputBg || t.surface,
                border: `1px solid ${phraseMatch ? '#22c55e' : typed.length > 0 ? (t.accent || t.text) : (t.borderStrong || t.border)}`,
                borderRadius: '8px',
                padding: '14px 16px',
                fontSize: '14px',
                fontFamily: 'Poppins, sans-serif',
                color: t.text,
                outline: 'none',
                resize: 'none',
                lineHeight: 1.6,
                transition: 'border-color 0.2s',
                boxSizing: 'border-box',
              }}
            />
            {phraseMatch && (
              <div style={{ position: 'absolute', right: '12px', top: '12px' }}>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M3 8.5L6.5 12L13 4" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            )}
          </div>

          <p style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '11px',
            color: phraseMatch ? '#22c55e' : (t.textTertiary || t.textSecondary),
            marginBottom: '20px',
          }}>
            {phraseMatch ? 'Statement confirmed' : `${Math.max(0, PHRASE.length - typed.length)} characters remaining`}
          </p>

          <button
            onClick={() => setStep(2)}
            disabled={!phraseMatch}
            style={{
              width: '100%',
              backgroundColor: phraseMatch ? t.buttonBg : 'transparent',
              color: phraseMatch ? t.buttonText : (t.textTertiary || t.textSecondary),
              padding: '14px',
              borderRadius: '12px',
              border: phraseMatch ? 'none' : `1px solid ${t.borderStrong || t.border}`,
              cursor: phraseMatch ? 'pointer' : 'not-allowed',
              fontSize: '15px',
              fontWeight: 600,
              fontFamily: 'Poppins, sans-serif',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => phraseMatch && (e.target.style.transform = 'translateY(-2px)')}
            onMouseLeave={(e) => phraseMatch && (e.target.style.transform = 'translateY(0)')}
          >Continue</button>

          <button
            onClick={onBack}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              color: t.textSecondary,
              padding: '12px',
              cursor: 'pointer',
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
              marginTop: '8px',
            }}
          >Back</button>
        </div>
      )}

      {/* Step 2: Enter wallet address */}
      {step === 2 && (
        <div>
          <p style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '13px',
            fontWeight: 600,
            color: t.text,
            marginBottom: '4px',
          }}>Your wallet address</p>
          <p style={{
            fontFamily: 'Poppins, sans-serif',
            fontSize: '12px',
            color: t.textTertiary || t.textSecondary,
            marginBottom: '12px',
          }}>Paste your Cardano wallet receive address (starts with addr...)</p>

          <input
            type="text"
            value={address}
            onChange={(e) => { setAddress(e.target.value); setError(''); }}
            placeholder="addr1q..."
            style={{
              width: '100%',
              backgroundColor: t.inputBg || t.surface,
              border: `1px solid ${validAddress ? '#22c55e' : address.length > 0 ? (t.accent || t.text) : (t.borderStrong || t.border)}`,
              borderRadius: '8px',
              padding: '14px 16px',
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
              color: t.text,
              outline: 'none',
              marginBottom: '6px',
              transition: 'border-color 0.2s',
              boxSizing: 'border-box',
            }}
          />

          {error && (
            <div style={{
              backgroundColor: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.25)',
              borderRadius: '8px',
              padding: '12px 16px',
              marginBottom: '12px',
              marginTop: '8px',
            }}>
              <p style={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: '13px',
                color: '#ef4444',
                marginBottom: '10px',
                lineHeight: 1.5,
              }}>{error}</p>
              <button
                onClick={handleSend}
                disabled={!validAddress || sending}
                style={{
                  background: 'none',
                  border: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '13px',
                  color: t.accent || t.text,
                  cursor: validAddress && !sending ? 'pointer' : 'not-allowed',
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                  fontWeight: 600,
                  padding: 0,
                }}
              >
                {sending ? 'Receiving...' : 'Receive 1 ADA'}
              </button>
            </div>
          )}

          {!error && (
            <p style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: '11px',
              color: t.textTertiary || t.textSecondary,
              marginBottom: '20px',
            }}>One-time only. 1 ADA will be sent from The Quarry treasury.</p>
          )}

          {!error && (
            <button
              onClick={handleSend}
              disabled={!validAddress || sending}
              style={{
                width: '100%',
                backgroundColor: validAddress && !sending ? t.buttonBg : 'transparent',
                color: validAddress && !sending ? t.buttonText : (t.textTertiary || t.textSecondary),
                padding: '14px',
                borderRadius: '12px',
                border: validAddress && !sending ? 'none' : `1px solid ${t.borderStrong || t.border}`,
                cursor: validAddress && !sending ? 'pointer' : 'not-allowed',
                fontSize: '15px',
                fontWeight: 600,
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => validAddress && !sending && (e.target.style.transform = 'translateY(-2px)')}
              onMouseLeave={(e) => validAddress && !sending && (e.target.style.transform = 'translateY(0)')}
            >
              {sending ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${t.borderStrong || t.border}`,
                    borderTopColor: t.accent || t.text,
                    borderRadius: '50%',
                    animation: 'quarrySpin 1s linear infinite',
                  }} />
                  Receiving...
                </>
              ) : 'Receive 1 ADA'}
            </button>
          )}

          <button
            onClick={() => { setStep(1); setError(''); }}
            style={{
              width: '100%',
              background: 'none',
              border: 'none',
              color: t.textSecondary,
              padding: '12px',
              cursor: 'pointer',
              fontSize: '13px',
              fontFamily: 'Poppins, sans-serif',
              marginTop: '8px',
            }}
          >Back</button>
        </div>
      )}

      <style>{`@keyframes quarrySpin { 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}


export default function ConnectWalletModal({ 
  show, 
  onClose,
  connectionStatus,
  connectionMessage,
  selectedWalletType,
  onConnectWallet
}) {
  const { t, isDark } = useTheme();
  const [showFaucet, setShowFaucet] = useState(false);

  // Reset faucet view when modal closes
  useEffect(() => {
    if (!show) setShowFaucet(false);
  }, [show]);

  if (!show) return null;

  return (
    <ModalShell onClose={onClose} maxWidth="550px" panelStyle={{ padding: '40px', borderRadius: '20px' }}>
        {/* Always-visible close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: '16px',
            right: '16px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '4px',
            color: t.textSecondary,
            transition: 'color 0.2s',
          }}
          onMouseEnter={(e) => e.target.style.color = t.text}
          onMouseLeave={(e) => e.target.style.color = t.textSecondary}
          aria-label="Close"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18"/><path d="M6 6l12 12"/>
          </svg>
        </button>

        {showFaucet ? (
          <AdaFaucetFlow 
            onBack={() => setShowFaucet(false)} 
            t={t} 
          />
        ) : (
          <>
            <h2 style={{ 
              fontFamily: 'Crimson Pro, serif', 
              fontSize: '40px', 
              marginBottom: '12px',
              color: t.text,
              textAlign: 'center'
            }}>
              Connect Wallet
            </h2>
            <p style={{ 
              color: t.textSecondary, 
              marginBottom: '32px',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '16px',
              textAlign: 'center'
            }}>
              Connect your Cardano wallet to start writing
            </p>

            {/* Instructions */}
            {!selectedWalletType && (
              <div style={{
                backgroundColor: t.modalBg,
                padding: '24px',
                borderRadius: '12px',
                marginBottom: '32px',
                border: '2px solid #f59e0b'
              }}>
                <p style={{ 
                  fontWeight: '700', 
                  marginBottom: '16px', 
                  color: t.text,
                  fontSize: '16px'
                }}>
                  Before connecting:
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {[
                    'Make sure you have Lace or Eternl browser extension installed',
                    'Your wallet should be unlocked and set up',
                    'Allow popups from this site if prompted',
                    'Approve the connection request in your wallet',
                    'Your wallet must have at least 1 ADA',
                  ].map((step, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                      <div style={{
                        backgroundColor: t.buttonBg,
                        color: t.buttonText,
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: '700',
                        flexShrink: 0
                      }}>
                        {idx + 1}
                      </div>
                      <p style={{ 
                        margin: 0, 
                        color: idx === 4 ? (t.accent || '#f59e0b') : t.text,
                        fontSize: '15px',
                        lineHeight: '1.6',
                        paddingTop: '3px',
                        fontWeight: idx === 4 ? 500 : 400,
                      }}>
                        {step}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <button
                onClick={() => onConnectWallet('lace')}
                disabled={connectionStatus === 'connecting'}
                style={{
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  padding: '20px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: connectionStatus === 'connecting' ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif',
                  opacity: connectionStatus === 'connecting' ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !connectionStatus && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => !connectionStatus && (e.target.style.transform = 'translateY(0)')}
              >
                {selectedWalletType === 'lace' && connectionStatus === 'connecting' ? 'Connecting to Lace...' : 'Connect with Lace'}
              </button>

              <button
                onClick={() => onConnectWallet('eternl')}
                disabled={connectionStatus === 'connecting'}
                style={{
                  backgroundColor: t.buttonBg,
                  color: t.buttonText,
                  padding: '20px',
                  borderRadius: '12px',
                  border: 'none',
                  cursor: connectionStatus === 'connecting' ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  fontWeight: '600',
                  fontFamily: 'Poppins, sans-serif',
                  opacity: connectionStatus === 'connecting' ? 0.5 : 1,
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => !connectionStatus && (e.target.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => !connectionStatus && (e.target.style.transform = 'translateY(0)')}
              >
                {selectedWalletType === 'eternl' && connectionStatus === 'connecting' ? 'Connecting to Eternl...' : 'Connect with Eternl'}
              </button>

              {!connectionStatus && (
                <button
                  onClick={onClose}
                  style={{
                    backgroundColor: 'transparent',
                    color: t.text,
                    padding: '16px',
                    borderRadius: '12px',
                    border: `2px solid ${t.borderStrong}`,
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontFamily: 'Poppins, sans-serif',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = t.accent;
                    e.target.style.backgroundColor = t.cream;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = t.borderStrong;
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  Cancel
                </button>
              )}

              {/* Footer links */}
              <div style={{ textAlign: 'center', marginTop: '12px' }}>
                <p style={{ fontSize: '13px', color: t.textSecondary, marginBottom: '8px' }}>Don't have a wallet?</p>
                <div style={{ marginBottom: '12px' }}>
                  <a
                    href="https://www.lace.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      fontSize: '14px', 
                      color: t.text, 
                      textDecoration: 'none',
                      fontWeight: '600'
                    }}
                  >
                    Get Lace
                  </a>
                  <span style={{ margin: '0 12px', color: t.borderStrong }}>&#8226;</span>
                  <a
                    href="https://eternl.io/"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      fontSize: '14px', 
                      color: t.text, 
                      textDecoration: 'none',
                      fontWeight: '600'
                    }}
                  >
                    Get Eternl
                  </a>
                </div>
                <button
                  onClick={() => setShowFaucet(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    color: t.accent || t.text,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: '3px',
                    opacity: 0.7,
                    padding: '4px',
                  }}
                >Need ADA to get started?</button>
              </div>
            </div>
          </>
        )}
    </ModalShell>
  );
}